import { useMemo, useState } from "react";
import { TrendUp, Table as TableIcon, Scales, Copy } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SectionTitle } from "@/components/PageHeader";

const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;
const median = (a) => {
  if (!a.length) return 0;
  const s = [...a].sort((x, y) => x - y); const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : Math.round((s[m - 1] + s[m]) / 2);
};

export default function HistoricalAnalysis({ points = [], onOpenJob }) {
  const [subjRate, setSubjRate] = useState("");
  const rated = useMemo(() => points.filter((p) => Number(p.rate) > 0).map((p) => ({ ...p, rate: Number(p.rate) })), [points]);

  const stats = useMemo(() => {
    if (!rated.length) return null;
    const rates = rated.map((p) => p.rate);
    return { count: rated.length, avg: Math.round(rates.reduce((a, b) => a + b, 0) / rates.length), min: Math.min(...rates), max: Math.max(...rates), med: median(rates) };
  }, [rated]);

  const trend = useMemo(() => rated
    .filter((p) => p.date && !Number.isNaN(Date.parse(p.date)))
    .map((p) => ({ t: Date.parse(p.date), date: p.date, rate: p.rate }))
    .sort((a, b) => a.t - b.t), [rated]);

  const byDistance = useMemo(() => [...rated].sort((a, b) => a.distance_km - b.distance_km), [rated]);

  const dedupe = useMemo(() => {
    const seen = new Map(); let dups = 0;
    for (const p of rated) {
      const key = `${Math.round(p.rate)}|${p.date || ""}|${p.lat.toFixed(3)}|${p.lng.toFixed(3)}`;
      if (seen.has(key)) dups++; else seen.set(key, 1);
    }
    return { unique: seen.size, dups };
  }, [rated]);

  if (!stats) return null;

  const sr = Number(subjRate);
  const justif = (() => {
    const base = `Based on ${stats.count} comparable${stats.count === 1 ? "" : "s"} in the vicinity, the prevailing rate ranges from ${inr(stats.min)} to ${inr(stats.max)}/sqft, with an average of ${inr(stats.avg)} and a median of ${inr(stats.med)}/sqft.`;
    if (!(sr > 0)) return `${base} Enter the subject's adopted rate above to check whether it is supported by these comparables.`;
    let verdict;
    if (sr < stats.min) verdict = `below the comparable band — the lower rate should be justified (e.g. inferior location, interior/smaller plot, encumbrance or distress).`;
    else if (sr > stats.max) verdict = `above the comparable band — the premium should be justified (e.g. corner/main-road frontage, superior construction, or a very recent transaction).`;
    else verdict = `within the comparable band of ${inr(stats.min)}–${inr(stats.max)}/sqft, so it is well supported by these records.`;
    const dev = Math.round(((sr - stats.avg) / stats.avg) * 100);
    return `${base} The adopted rate of ${inr(sr)}/sqft is ${verdict} It is ${dev >= 0 ? "+" : ""}${dev}% versus the average.`;
  })();

  return (
    <div className="space-y-4" data-testid="hist-analysis">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="hist-stats">
        {[["Comparables", stats.count], ["Average", `${inr(stats.avg)}/sqft`], ["Median", `${inr(stats.med)}/sqft`], ["Range", `${inr(stats.min)}–${inr(stats.max)}`]].map(([l, v]) => (
          <Card key={l} className="p-3 border-border"><div className="text-[11px] uppercase tracking-wide text-muted-foreground">{l}</div><div className="text-base font-bold text-primary mt-0.5">{v}</div></Card>
        ))}
      </div>

      <Card className="p-4 border-border">
        <SectionTitle icon={TrendUp}>Rate trend in the vicinity</SectionTitle>
        {trend.length < 2 ? <p className="text-sm text-muted-foreground mt-2">Not enough dated comparables to plot a trend.</p> : (
          <div style={{ width: "100%", height: 260 }} data-testid="hist-trend-chart">
            <ResponsiveContainer>
              <LineChart data={trend} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} width={62} tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(v) => [`${inr(v)}/sqft`, "Rate"]} />
                <Line type="monotone" dataKey="rate" stroke="#0A2540" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <Card className="p-0 border-border overflow-hidden">
        <div className="p-4 pb-2"><SectionTitle icon={TableIcon}>Rates by distance from the point</SectionTitle></div>
        <div className="max-h-72 overflow-auto" data-testid="hist-distance-table">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-muted text-muted-foreground"><tr>
              <th className="text-left px-4 py-2 font-medium">Type</th>
              <th className="text-right px-4 py-2 font-medium">Rate /sqft</th>
              <th className="text-right px-4 py-2 font-medium">Distance</th>
              <th className="text-left px-4 py-2 font-medium">Date</th>
              <th className="text-left px-4 py-2 font-medium">Reference</th>
            </tr></thead>
            <tbody>
              {byDistance.map((p) => (
                <tr key={p.id} className="border-t border-border" data-testid={`hist-row-${p.id}`}>
                  <td className="px-4 py-2">{p.property_type || "—"}</td>
                  <td className="px-4 py-2 text-right font-semibold text-primary">{inr(p.rate)}</td>
                  <td className="px-4 py-2 text-right">{p.distance_km} km</td>
                  <td className="px-4 py-2">{p.date || "—"}</td>
                  <td className="px-4 py-2 max-w-[180px] truncate">{onOpenJob && !p.is_legacy ? <button type="button" onClick={() => onOpenJob(p.id)} className="text-accent hover:underline" data-testid={`hist-open-${p.id}`}>{p.reference || "Open"}</button> : <span className="text-muted-foreground">{p.reference || "—"}{p.is_legacy ? " (legacy)" : ""}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="p-4 border-border">
        <SectionTitle icon={Scales}>Possible rate justification</SectionTitle>
        <div className="flex items-end gap-3 mt-2 mb-3 max-w-xs">
          <div className="space-y-1 flex-1"><Label className="text-xs">Subject adopted rate (₹/sqft)</Label><Input value={subjRate} onChange={(e) => setSubjRate(e.target.value)} placeholder="e.g. 9500" data-testid="hist-subject-rate" /></div>
        </div>
        <p className="text-sm text-foreground leading-relaxed" data-testid="hist-justification">{justif}</p>
      </Card>

      <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="hist-dedupe">
        <Copy size={14} /> {dedupe.unique} unique comparable{dedupe.unique === 1 ? "" : "s"}{dedupe.dups ? ` · ${dedupe.dups} near-duplicate record${dedupe.dups === 1 ? "" : "s"} grouped out` : " · no duplicates detected"}
      </div>
    </div>
  );
}
